import { Combobox } from '@headlessui/react';
import { ChangeEvent, FC, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import Chip from '../chip/chip';
import Icon from '../icon/icon';

type SelectColors = 'primary' | 'variant';

type Option = {
    value: string;
    label: string;
    disable?: boolean;
    color?: string;
};

interface ComboboxAutoCompleteI {
    name: string;
    values: string | string[];
    onChange(values: string | string[]): void;
    onInputChange(event: ChangeEvent<HTMLInputElement>): void;
    filterFunction?(query: string, options: Option[]): Option[];
    options: Option[];
    query: string;
    label?: string;
    color?: SelectColors;
    handleDeleteChip?(values: string): void;
    containerClasses?: string;
    optionsClasses?: string;
    inputContainerClasses?: string;
    addUnknownOption?: boolean;
    testId?: string;
    required?: boolean;
    placeholder?: string;
    labelClasses?: string;
    disabled?: boolean;
}

export const ComboboxAutoComplete: FC<ComboboxAutoCompleteI> = ({ addUnknownOption = true, ...props }) => {
    const comboBtn = useRef<HTMLButtonElement | null>(null);
    const onSearchInputFocus = () => {
        if (props.options.length) {
            comboBtn.current?.click();
        }
    };
    const findOptionAccordingValue = (item: string) => {
        const itemFonded = props.options.find((i) => {
            return i.label === item;
        });
        const defaultItem =
            item && item.length > 0 ? ({ label: item, value: item, color: '#00BCD4' } as Option) : props.options[0];

        return itemFonded || defaultItem;
    };
    const selectColors: Record<SelectColors, string> = {
        primary: 'ui-open:border-primary-500',
        variant: 'ui-open:border-variant-800',
    };
    const containerClasses = twMerge(
        'flex flex-reverse flex-wrap justify-start w-full p-2 group rounded',
        'text-sm font-roboto font-normal text-surface-high',
        'border border-gray-100 disabled:border-gray-100',
        'ui-open:rounded-b-none relative',
        selectColors[props.color || 'primary'],
        props.inputContainerClasses,
    );
    const optionSurfaceClasses = twMerge(
        'flex justify-start items-center p-2 peer h-8 bg-white hover:bg-gray-50 text-sm font-roboto font-normal text-surface-high leading-8 cursor-pointer ui-selected:bg-primary-50',
    );

    const optionsDigested = props.filterFunction ? props.filterFunction(props.query, props.options) : props.options;

    return Array.isArray(props.values) ? (
        // Multiple
        <Combobox
            as="div"
            name={props.name}
            value={props.values}
            onChange={props.onChange}
            multiple
            className={twMerge('relative', props.containerClasses)}
            disabled={props.disabled ?? false}
        >
            {props.label && (
                <Combobox.Label className={twMerge('font-medium text-sm pb-2', props.labelClasses)}>
                    {props.label}
                    {props.required && <span className="text-red-600"> * </span>}
                </Combobox.Label>
            )}
            <div className={containerClasses}>
                {
                    <div className="flex flex-wrap grow-1">
                        {props.values && props.options ? (
                            props.values.length <= 3 ? (
                                props.values.map(findOptionAccordingValue).map((item, index) => {
                                    return (
                                        <Chip
                                            key={item.label + index}
                                            className="m-0"
                                            text={item.label as string}
                                            size="xxs"
                                            color={item.color}
                                            onClose={(evt) => {
                                                evt?.preventDefault();
                                                props.handleDeleteChip && props.handleDeleteChip(item.label);
                                            }}
                                            disabled={props.disabled ?? false}
                                        />
                                    );
                                })
                            ) : (
                                <Chip
                                    key={`Seleccionadas ${props.values.length}`}
                                    className="m-0"
                                    text={`Seleccionadas ${props.values.length}`}
                                    size="xxs"
                                    onClose={(evt) => {
                                        evt?.preventDefault();
                                        props.handleDeleteChip && props.handleDeleteChip('ALL');
                                    }}
                                    disabled={props.disabled ?? false}
                                />
                            )
                        ) : (
                            // chip para nueva opcion
                            props.values.map((label) => {
                                return (
                                    <Chip
                                        className="mb-0"
                                        text={label}
                                        size="xxs"
                                        onClose={(evt) => {
                                            evt?.preventDefault();
                                            console.log('delete');
                                        }}
                                    />
                                );
                            })
                        )}
                    </div>
                }
                <Combobox.Input
                    className="outline-none pl-2 grow-[1]"
                    onChange={props.onInputChange}
                    value={props.query}
                    onFocus={onSearchInputFocus}
                    autoComplete="off"
                    data-test-id={`${props.testId}-combo-input`}
                    placeholder={props.placeholder}
                />
                <Combobox.Button className="hidden" ref={comboBtn}></Combobox.Button>
            </div>
            <Combobox.Options
                className={twMerge(
                    'absolute py-2 bg-white rounded-b shadow-[0px_3px_10px_0px_rgba(20,115,247,0.12)] z-10 max-h-[280px] overflow-auto',
                    props.optionsClasses,
                )}
            >
                {optionsDigested.length > 0 ? (
                    optionsDigested.map((option, index) => (
                        <Combobox.Option
                            key={option.value}
                            value={option.label}
                            className={optionSurfaceClasses}
                            data-test-id={`${props.testId}-combo-option`}
                            data-index={index}
                            disabled={props.disabled ?? false}
                        >
                            {({ selected }) => (
                                <>
                                    <Icon
                                        size={16}
                                        className="mr-2"
                                        name={selected ? 'ri-checkbox-line' : 'ri-checkbox-blank-line'}
                                    />
                                    {option.label}
                                </>
                            )}
                        </Combobox.Option>
                    ))
                ) : addUnknownOption ? (
                    <Combobox.Option
                        key={'new'}
                        value={props.query}
                        className={optionSurfaceClasses}
                        data-test-id={`${props.testId}-combo-option`}
                    >
                        {props.query} +
                    </Combobox.Option>
                ) : null}
            </Combobox.Options>
        </Combobox>
    ) : (
        <Combobox
            as="div"
            name={props.name}
            value={props.values}
            onChange={props.onChange}
            className={twMerge(props.containerClasses)}
        >
            {props.label && <Combobox.Label className="font-bold pb-2">{props.label}</Combobox.Label>}
            <div className={containerClasses}>
                <Combobox.Input
                    className="outline-none pl-2 grow-[2]"
                    value={props.query}
                    onChange={props.onInputChange}
                    data-test-id={`${props.testId}-combo-input`}
                    placeholder={props.placeholder}
                />
            </div>
            <Combobox.Options
                className={twMerge(
                    'py-2 bg-white rounded-b shadow-[0px_3px_10px_0px_rgba(20,115,247,0.12)] h-[200px] overflow-y',
                    props.optionsClasses,
                )}
            >
                {optionsDigested.length > 0 ? (
                    optionsDigested.map((option) => (
                        <Combobox.Option
                            className="flex justify-start items-center p-2 peer h-8 bg-white hover:bg-gray-50 text-sm font-roboto font-normal text-surface-high leading-8 cursor-pointer"
                            key={option.value}
                            value={option.label}
                            data-test-id={`${props.testId}-combo-option`}
                        >
                            {option.label}
                        </Combobox.Option>
                    ))
                ) : (
                    <Combobox.Option key={'new'} value={props.query} data-test-id={`${props.testId}-combo-option`}>
                        {props.query} +
                    </Combobox.Option>
                )}
            </Combobox.Options>
        </Combobox>
    );
};

export default ComboboxAutoComplete;
